import type { ListenersMap } from "./types";
import type { HubName } from "./useAxureCloudHubsConnection";
import { onBeforeMount, onBeforeUnmount } from "vue";
import { useAxureCloudHubsConnection } from "./useAxureCloudHubsConnection";

export interface UseAxureCloudHubOptions<TPayload> {
  listeners?: ListenersMap<TPayload>;
}

export function throttleByArgs<T extends (...args: any[]) => any>(
  fn: T,
  { timeout, maxSize }: { timeout: number; maxSize: number },
) {
  // key → last call timestamp
  const lastCalls = new Map<string, number>();

  return function (...args: Parameters<T>): ReturnType<T> | void {
    const key = JSON.stringify(args);
    const now = Date.now();
    const last = lastCalls.get(key);

    // Throttle if same arguments within timeout
    if (last !== undefined && now - last < timeout) {
      return;
    }

    lastCalls.set(key, now);

    // Enforce max size (FIFO)
    if (lastCalls.size > maxSize) {
      // remove oldest entry
      const oldestKey = lastCalls.keys().next().value;
      if (oldestKey !== undefined) {
        lastCalls.delete(oldestKey);
      }
    }

    return fn(...args);
  };
}

export function useAxureCloudHub<TPayload>(name: HubName, options: UseAxureCloudHubOptions<TPayload> = {}) {
  const connection = useAxureCloudHubsConnection();

  async function joinGroup(groupName: string) {
    await connection.addToGroup(name, groupName);
  }

  async function leaveGroup(groupName: string) {
    await connection.removeFromGroup(name, groupName);
  }

  const joinGroupThrottled = throttleByArgs(joinGroup, { timeout: 3000, maxSize: 10 });
  const leaveGroupThrottled = throttleByArgs(leaveGroup, { timeout: 3000, maxSize: 10 });

  onBeforeMount(async () => {
    await connection.connectToHub(name);
    if (options.listeners) {
      await connection.addHubListeners(name, options.listeners);
    }
  });

  onBeforeUnmount(async () => {
    if (options.listeners) {
      await connection.removeHubListeners(name, options.listeners);
    }
  });

  return {
    joinGroup: joinGroupThrottled,
    leaveGroup: leaveGroupThrottled,
  };
}
